/**
 * Shared UI component for UI Topbar.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';


import { UiButtonComponent, UiCardComponent, UiSelectComponent, UiSelectOption } from '@shared/ui';
import { ThemeService } from '../../../core/services/theme.service';

import { BreadcrumbItem, UiTopbarLanguage, UiTopbarUser } from './ui-topbar.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

interface UiTopbarNotification {
  id: string;
  title: string;
  description: string;
  meta: string;
  type: 'stock' | 'transfer' | 'approval' | 'task' | 'system';
  read: boolean;
  expanded: boolean;
}

type UiTopbarNotificationTab = 'all' | 'unread' | 'read';

@Component({
  selector: 'ui-topbar',
  standalone: true,
  imports: [RouterLink, UiSelectComponent, UiButtonComponent, UiCardComponent, TranslatePipe, UiLabelTranslatePipe],
  templateUrl: './ui-topbar.component.html',
  styleUrls: ['./ui-topbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTopbarComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly themeService = inject(ThemeService);
  private readonly notificationPageSize = 15;
  readonly breadcrumbs = input<BreadcrumbItem[]>([{ label: 'Dashboard', path: '/dashboard' }]);
  readonly user = input<UiTopbarUser>({
    name: 'Demo User',
    initials: 'DU',
    language: 'en'
  });
  readonly themeInput = input<'light' | 'dark'>('light');

  readonly profileClicked = output<void>();
  readonly logoutClicked = output<void>();
  readonly languageChanged = output<UiTopbarLanguage>();
  readonly backClicked = output<void>();
  readonly themeChanged = output<'light' | 'dark'>();

  readonly menuOpen = signal(false);
  readonly notificationDrawerOpen = signal(false);
  readonly theme = computed(() => this.themeService.theme());
  readonly activeNotificationTab = signal<UiTopbarNotificationTab>('all');
  readonly notificationLimit = signal(this.notificationPageSize);
  readonly notifications = signal<UiTopbarNotification[]>(this.buildMockNotifications());
  readonly unreadNotificationCount = computed(() => this.notifications().filter((item) => !item.read).length);
  readonly filteredNotifications = computed(() => {
    const tab = this.activeNotificationTab();

    if (tab === 'unread') {
      return this.notifications().filter((item) => !item.read);
    }

    if (tab === 'read') {
      return this.notifications().filter((item) => item.read);
    }

    return this.notifications();
  });
  readonly visibleNotifications = computed(() => this.filteredNotifications().slice(0, this.notificationLimit()));
  readonly hasMoreNotifications = computed(() => this.notificationLimit() < this.filteredNotifications().length);
  readonly unreadNotificationBadge = computed(() => {
    const count = this.unreadNotificationCount();
    return count > 99 ? '99+' : String(count);
  });
  readonly languageOptions: UiSelectOption[] = [
    { label: 'English', value: 'en' },
    { label: 'French', value: 'fr' }
  ];

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    this.closeNotifications();
    this.closeMenu();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!event.target) {
      return;
    }

    const target = event.target as Node;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.closeMenu();
      this.closeNotifications();
    }
  }

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  toggleTheme(): void {
    const newTheme = this.theme() === 'light' ? 'dark' : 'light';
    this.themeService.setTheme(newTheme);
    this.themeChanged.emit(newTheme);
  }

  onProfileClicked(): void {
    this.profileClicked.emit();
    this.closeMenu();
  }

  onBackClicked(): void {
    this.backClicked.emit();
  }

  parseCrumbPath(path: string): string {
    return path.split('?')[0];
  }

  parseCrumbQuery(path: string): Record<string, string> | null {
    const qs = path.split('?')[1];
    if (!qs) return null;
    const params: Record<string, string> = {};
    qs.split('&').forEach((pair) => {
      const [key, value] = pair.split('=');
      if (key) params[decodeURIComponent(key)] = decodeURIComponent(value ?? '');
    });
    return params;
  }

  onLanguageChange(value: string): void {
    if (value === 'en' || value === 'fr') {
      this.languageChanged.emit(value);
    }
  }

  onLogoutClicked(): void {
    this.logoutClicked.emit();
    this.closeMenu();
  }

  toggleNotifications(): void {
    this.notificationDrawerOpen.set(!this.notificationDrawerOpen());
  }

  closeNotifications(): void {
    this.notificationDrawerOpen.set(false);
  }

  setNotificationTab(tab: UiTopbarNotificationTab): void {
    this.activeNotificationTab.set(tab);
    this.notificationLimit.set(this.notificationPageSize);
  }

  toggleNotification(id: string): void {
    // Clicking a notification marks it as read and expands/collapses details.
    this.notifications.update((items) =>
      items.map((item) => (item.id === id ? { ...item, read: true, expanded: !item.expanded } : item))
    );
  }

  markAllAsRead(): void {
    // Keep "mark all" idempotent so reopening does not flip messages back to unread.
    this.notifications.update((items) => items.map((item) => ({ ...item, read: true })));
  }

  clearNotifications(): void {
    this.notifications.set([]);
    this.notificationLimit.set(this.notificationPageSize);
  }

  loadMoreNotifications(event?: Event): void {
    event?.stopPropagation();
    this.notificationLimit.update((limit) => Math.min(limit + this.notificationPageSize, this.filteredNotifications().length));
  }

  private buildMockNotifications(): UiTopbarNotification[] {
    const titles = [
      'Low stock warning',
      'Transfer approved',
      'Fuel request pending',
      'Agent task delayed',
      'Supervisor assignment updated',
      'Banking note submitted',
      'Campaign stock reserved',
      'Vehicle document expiring',
      'POS stock entry added',
      'Access permission changed',
      'Maintenance approval needed',
      'Agency target reached'
    ];
    const modules = ['Central Inventory', 'Agencies', 'Logistics', 'Agents', 'BTL', 'POS', 'Access Control'];
    const types: UiTopbarNotification['type'][] = ['stock', 'transfer', 'approval', 'task', 'system'];

    return Array.from({ length: 30 }, (_, index) => ({
      id: `n-${index + 1}`,
      title: titles[index % titles.length],
      description: `${modules[index % modules.length]} notification ${index + 1} is ready for admin review in the development mock queue.`,
      meta: `${modules[index % modules.length]} - ${index < 12 ? `${(index + 1) * 5} min ago` : 'Yesterday'}`,
      type: types[index % types.length],
      read: index % 4 === 0,
      expanded: false
    }));
  }
}
